/**
 * Serviço de autenticação para comunicação com a API.
 */

import { api } from './client'
import type { AuthGuard } from '@/lib/jwt'
import type {
  ChangePasswordInput,
  InstitutionMe,
  InstitutionMeResponse,
  InstitutionRegisterRequest,
  InstitutionRegisterResponse,
} from '@/types/api'
import {
  buildInstitutionProfileFormData,
  type InstitutionProfileUpdateInput,
} from '@/lib/auth'

/** Contexto para escolher o endpoint de troca de senha (admin vs institution). */
export interface ChangePasswordContext {
  guard: AuthGuard
  institutionId: string | null
}

/**
 * Registra uma nova instituição.
 */
export async function registerInstitution(
  data: InstitutionRegisterRequest
): Promise<InstitutionRegisterResponse> {
  return api.post<InstitutionRegisterResponse>(
    '/api/institution/register',
    data,
    { requireAuth: false }
  )
}

/**
 * Obtém os dados da instituição autenticada (ou da instituição impersonada pelo admin).
 * Usa o client que envia X-Institution-Id quando há instituição selecionada.
 */
export async function getMe(): Promise<InstitutionMe> {
  const response = await api.get<InstitutionMeResponse>('/api/institution/me', {
    requireAuth: true,
  })
  return response.institution
}

/**
 * Grava a campanha ativa do painel para a instituição (autenticada ou impersonada).
 */
export async function updateLastSelectedCampaign(campaignId: number): Promise<InstitutionMe> {
  const data = await api.put<{ institution: InstitutionMe }>(
    '/api/institution/last-selected-campaign',
    { campaign_id: campaignId },
    { requireAuth: true }
  )
  return data.institution
}

/**
 * Atualiza o perfil da instituição autenticada (ou da instituição impersonada pelo admin).
 * Usa o client que envia X-Institution-Id quando há instituição selecionada.
 */
export async function updateInstitutionProfileViaClient(
  input: InstitutionProfileUpdateInput
): Promise<unknown> {
  return api.post<unknown>('/api/institution/profile', buildInstitutionProfileFormData(input), {
    requireAuth: true,
  })
}

/**
 * Troca a senha do usuário autenticado.
 * Admin sempre usa /api/admin/auth/password (mesmo impersonando).
 * Institution e colaborador usam /api/institution/auth/password.
 */
export async function changePassword(
  data: ChangePasswordInput,
  context: ChangePasswordContext
): Promise<{ message: string }> {
  const endpoint =
    context.guard === 'admin'
      ? '/api/admin/auth/password'
      : '/api/institution/auth/password'
  return api.put<{ message: string }>(endpoint, data, { requireAuth: true })
}
